import {
  AccountCircle,
  Apartment,
  ArticleOutlined,
  Audiotrack,
  Facebook,
  Instagram,
  LocalShipping,
  LocationOnOutlined,
  PersonOutline,
  PhotoOutlined,
  Storefront,
  SupportAgent,
  VideocamOutlined,
  WhatsApp,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { ChatModel } from "../../data/models/Chat/ChatModel";
import { MessageTypeEnum } from "../../data/models/Chat/MessageModel";
import { formatChatDate } from "../../utils/DateTool";
import { formatWhatsappRecipient } from "../../utils/WhatsappIdentity";

const getChannelConfig = (channel?: string) => {
  const normalizedChannel = String(channel || "whatsapp").toLowerCase();

  if (normalizedChannel.includes("instagram")) {
    return {
      label: "Instagram",
      icon: <Instagram sx={{ fontSize: 11 }} />,
      color: "#E1306C",
      background: "rgba(225, 48, 108, 0.12)",
    };
  }

  if (normalizedChannel.includes("facebook")) {
    return {
      label: "Facebook",
      icon: <Facebook sx={{ fontSize: 11 }} />,
      color: "#1877F2",
      background: "rgba(24, 119, 242, 0.12)",
    };
  }

  return {
    label: "WhatsApp",
    icon: <WhatsApp sx={{ fontSize: 11 }} />,
    color: "#25D366",
    background: "rgba(37, 211, 102, 0.14)",
  };
};

const formatLastMessagePreview = (value?: string): string => {
  const normalized = String(value || "").trim();
  const mediaLabels: Record<string, string> = {
    _AUDIO_PENDING_: "Audio",
    _AUDIO_: "Audio",
    _IMAGE_PENDING_: "Imagen",
    _IMAGE_: "Imagen",
    _VIDEO_PENDING_: "Video",
    _VIDEO_: "Video",
    _DOCUMENT_PENDING_: "Documento",
    _DOCUMENT_: "Documento",
    _STICKER_PENDING_: "Sticker",
    _STICKER_: "Sticker",
  };
  return mediaLabels[normalized] || normalized;
};

const getPreviewByMessageType = (messageType?: MessageTypeEnum | string | null) => {
  switch (messageType) {
    case MessageTypeEnum.AUDIO:
      return { label: "Audio", icon: <Audiotrack sx={{ fontSize: 12 }} /> };
    case MessageTypeEnum.IMAGE:
      return { label: "Imagen", icon: <PhotoOutlined sx={{ fontSize: 12 }} /> };
    case MessageTypeEnum.VIDEO:
      return { label: "Video", icon: <VideocamOutlined sx={{ fontSize: 12 }} /> };
    case MessageTypeEnum.DOCUMENT:
      return { label: "Documento", icon: <ArticleOutlined sx={{ fontSize: 12 }} /> };
    case MessageTypeEnum.STICKER:
      return { label: "Sticker", icon: <WhatsApp sx={{ fontSize: 12 }} /> };
    case MessageTypeEnum.LOCATION:
      return { label: "Ubicacion", icon: <LocationOnOutlined sx={{ fontSize: 12 }} /> };
    case MessageTypeEnum.CONTACTS:
      return { label: "Contacto", icon: <PersonOutline sx={{ fontSize: 12 }} /> };
    case MessageTypeEnum.INTERACTIVE:
      return { label: "Interacción", icon: <WhatsApp sx={{ fontSize: 12 }} /> };
    default:
      return null;
  }
};

const getLastMessagePreview = (
  value?: string,
  messageType?: MessageTypeEnum | string | null
) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return null;
  }

  const explicitPreview = getPreviewByMessageType(messageType);
  if (explicitPreview && messageType !== MessageTypeEnum.TEXT) {
    return explicitPreview;
  }

  const previewMap: Record<
    string,
    {
      label: string;
      icon: React.ReactNode;
    }
  > = {
    _AUDIO_PENDING_: {
      label: "Audio",
      icon: <Audiotrack sx={{ fontSize: 12 }} />,
    },
    _AUDIO_: {
      label: "Audio",
      icon: <Audiotrack sx={{ fontSize: 12 }} />,
    },
    _IMAGE_PENDING_: {
      label: "Imagen",
      icon: <PhotoOutlined sx={{ fontSize: 12 }} />,
    },
    _IMAGE_: {
      label: "Imagen",
      icon: <PhotoOutlined sx={{ fontSize: 12 }} />,
    },
    _VIDEO_PENDING_: {
      label: "Video",
      icon: <VideocamOutlined sx={{ fontSize: 12 }} />,
    },
    _VIDEO_: {
      label: "Video",
      icon: <VideocamOutlined sx={{ fontSize: 12 }} />,
    },
    _DOCUMENT_PENDING_: {
      label: "Documento",
      icon: <ArticleOutlined sx={{ fontSize: 12 }} />,
    },
    _DOCUMENT_: {
      label: "Documento",
      icon: <ArticleOutlined sx={{ fontSize: 12 }} />,
    },
    _STICKER_PENDING_: {
      label: "Sticker",
      icon: <WhatsApp sx={{ fontSize: 12 }} />,
    },
    _STICKER_: {
      label: "Sticker",
      icon: <WhatsApp sx={{ fontSize: 12 }} />,
    },
    _LOCATION_: {
      label: "Ubicacion",
      icon: <LocationOnOutlined sx={{ fontSize: 12 }} />,
    },
  };

  if (previewMap[normalized]) {
    return previewMap[normalized];
  }

  if (/^https?:\/\//i.test(normalized)) {
    const lowerValue = normalized.toLowerCase();
    if (
      /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(lowerValue) ||
      lowerValue.includes("firebasestorage.googleapis.com")
    ) {
      return {
        label: "Imagen",
        icon: <PhotoOutlined sx={{ fontSize: 12 }} />,
      };
    }

    if (/\.(mp4|mov|webm|m4v)(\?|$)/i.test(lowerValue)) {
      return {
        label: "Video",
        icon: <VideocamOutlined sx={{ fontSize: 12 }} />,
      };
    }

    if (/\.(mp3|ogg|wav|m4a|aac|webm)(\?|$)/i.test(lowerValue)) {
      return {
        label: "Audio",
        icon: <Audiotrack sx={{ fontSize: 12 }} />,
      };
    }

    if (/\.(pdf|docx?|xlsx?|pptx?|txt|csv)(\?|$)/i.test(lowerValue)) {
      return {
        label: "Documento",
        icon: <ArticleOutlined sx={{ fontSize: 12 }} />,
      };
    }

    return {
      label: "Archivo",
      icon: <ArticleOutlined sx={{ fontSize: 12 }} />,
    };
  }

  return {
    label: formatLastMessagePreview(normalized),
    icon: null,
  };
};

const CONTACT_AVATAR_ICON_PREFIX = "lypro-icon:";

const getContactAvatarPresentation = (value?: string) => {
  const normalized = String(value || "");
  if (!normalized.startsWith(CONTACT_AVATAR_ICON_PREFIX)) {
    return null;
  }
  const id = normalized.slice(CONTACT_AVATAR_ICON_PREFIX.length);
  const iconSx = { fontSize: 17 };
  switch (id) {
    case "person-1":
      return {
        bg: "#DBEAFE",
        color: "#1D4ED8",
        icon: <PersonOutline sx={iconSx} />,
      };
    case "person-2":
      return {
        bg: "#EDE9FE",
        color: "#7C3AED",
        icon: <AccountCircle sx={iconSx} />,
      };
    case "business-1":
      return {
        bg: "#CCFBF1",
        color: "#0F766E",
        icon: <Storefront sx={iconSx} />,
      };
    case "business-2":
      return {
        bg: "#FEF3C7",
        color: "#B45309",
        icon: <Apartment sx={iconSx} />,
      };
    case "service-1":
      return {
        bg: "#FEE2E2",
        color: "#DC2626",
        icon: <LocalShipping sx={iconSx} />,
      };
    case "service-2":
      return {
        bg: "#DBEAFE",
        color: "#1D4ED8",
        icon: <SupportAgent sx={iconSx} />,
      };
    default:
      return null;
  }
};

export const ChatItem: React.FC<{
  chat: ChatModel;
  selectedChat: ChatModel | undefined;
  handleSelectChat: (chat: ChatModel) => void;
  handleOpenProfile: (chat: ChatModel) => void;
}> = ({ chat, selectedChat, handleSelectChat, handleOpenProfile }) => {
  const isSelected = selectedChat?.phoneNumber === chat.phoneNumber;
  const channel = getChannelConfig(chat.channel);
  const contactAvatar = getContactAvatarPresentation(chat.profilePictureUrl);
  const lastMessagePreview = getLastMessagePreview(
    chat.lastMessage,
    chat.lastMessageType
  );
  const maposName = chat.maposUser?.fullName?.trim();
  const primaryName =
    chat.customName || maposName || chat.fullName || chat.displayName || chat.phoneNumber || "?";
  const whatsappProfileName =
    maposName && chat.displayName?.trim() && chat.displayName.trim() !== maposName
      ? chat.displayName.trim()
      : undefined;

  return (
    <>
      <ListItemButton
        onClick={() => handleSelectChat(chat)}
        sx={{
          py: 0.75,
          px: 1.55,
          my: 0.25,
          borderRadius: 1,
          alignItems: "center",
          color: "text.primary",
          overflow: "hidden",
          bgcolor: isSelected ? "rgba(138, 0, 204, 0.16)" : "transparent",
          "&:hover": {
            bgcolor: isSelected ? "rgba(138, 0, 204, 0.2)" : "action.hover",
          },
        }}
      >
        <ListItemAvatar sx={{ minWidth: 39 }}>
          <IconButton
            size="small"
            aria-label={`Ver perfil de ${primaryName}`}
            onClick={(event) => {
              event.stopPropagation();
              handleOpenProfile(chat);
            }}
            sx={{
              p: 0,
              borderRadius: "50%",
              "&:hover": {
                bgcolor: "transparent",
              },
            }}
          >
            <Avatar
              src={contactAvatar ? undefined : chat.profilePictureUrl || undefined}
              sx={{
                width: 31,
                height: 31,
                fontSize: 12.75,
                fontWeight: 700,
                bgcolor: contactAvatar
                  ? contactAvatar.bg
                  : isSelected
                  ? "primary.main"
                  : "grey.400",
                color: contactAvatar ? contactAvatar.color : "#fff",
                boxShadow: isSelected ? "0 0 0 2px rgba(138,0,204,0.18)" : "none",
              }}
            >
              {contactAvatar
                ? contactAvatar.icon
                : primaryName.charAt(0)}
            </Avatar>
          </IconButton>
        </ListItemAvatar>

        <ListItemText
          sx={{ my: 0, minWidth: 0 }}
          primary={
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 12.3 }} noWrap>
                {primaryName}
              </Typography>
              {whatsappProfileName && (
                <Typography
                  component="div"
                  sx={{ color: "text.secondary", fontSize: 9.6, lineHeight: 1.1 }}
                  noWrap
                >
                  WhatsApp: {whatsappProfileName}
                </Typography>
              )}
            </Box>
          }
          secondary={
            <Box sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.6}
                sx={{ minWidth: 0, mb: 0.1 }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: 9.8,
                    lineHeight: 1.15,
                    minWidth: 0,
                  }}
                  noWrap
                >
                  {formatWhatsappRecipient(chat.waId || chat.phoneNumber, "Sin número")}
                </Typography>
                <Chip
                  size="small"
                  icon={channel.icon}
                  label={channel.label}
                  sx={{
                    height: 15,
                    maxWidth: 82,
                    "& .MuiChip-label": {
                      px: 0.6,
                      fontSize: 9,
                      fontWeight: 700,
                    },
                    "& .MuiChip-icon": {
                      ml: 0.5,
                      mr: -0.25,
                      color: channel.color,
                    },
                    color: channel.color,
                    bgcolor: channel.background,
                    borderRadius: 999,
                  }}
                />
              </Stack>
              <Typography
                component="div"
                sx={{
                  mt: 0.2,
                  color: "text.secondary",
                  fontSize: 10.6,
                  lineHeight: 1.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {lastMessagePreview?.icon ? (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: 0,
                      verticalAlign: "middle",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        color: "text.secondary",
                        flexShrink: 0,
                      }}
                    >
                      {lastMessagePreview.icon}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {lastMessagePreview.label}
                    </Box>
                  </Box>
                ) : (
                  lastMessagePreview?.label || ""
                )}
              </Typography>
            </Box>
          }
        />

        <Typography
          sx={{
            ml: 0.85,
            mt: 0.35,
            alignSelf: "flex-start",
            flexShrink: 0,
            color: "text.secondary",
            fontSize: 9.8,
          }}
        >
          {chat.createdAt ? formatChatDate(chat.lastMessageDate ?? chat.createdAt) : ""}
        </Typography>
      </ListItemButton>
      <Divider />
    </>
  );
};
